/**
 * Esteiras (P-EST-1): o quadro em colunas, o cartao, o historico do cartao, as
 * prioridades e o prazo por coluna.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3, e
 * docs/ADR/0040-esteiras-configuraveis.md.
 */

import { query, type ListParams, type Money } from './comum';

import { api, requestPage } from '../client';

export interface PipeSummary {
  id: string;
  key: string;
  name: string;
  icon_name: string;
  sort_order: number;
  /** Etapa da operacao em que a operacao tem cartao aqui. Vazio, nenhuma. */
  operation_stage: string;
  analysis_area: string;
  notify_email: string;
  /** Cartoes abertos (countCards do OnPoint). */
  open_cards: number;
  /**
   * A raia do motor de mesma chave abre cartao nesta esteira (P-EST-2). Opcional
   * para respostas antigas e simulacoes de teste.
   */
  engine_cards_enabled?: boolean;
  /** A esteira e uma raia do motor (credito, pep, subscricao). */
  engine_lane?: boolean;
}

export interface PipeJustification {
  can_add: boolean;
  is_required: boolean;
  allowed_in_step: boolean;
}

export type PipeStepType =
  'inicial' | 'andamento' | 'aprovado' | 'reprovado' | 'cancelado';

export interface PipeStep {
  id: string;
  pipe_id: string;
  key: string;
  name: string;
  sort_order: number;
  type: PipeStepType;
  is_final: boolean;
  operation_stage: string;
  analysis_status: string;
  pending_link: string;
  sla_days: number | null;
  sla_calendar: 'uteis' | 'corridos';
  auto_cancel: boolean;
  cards: number;
  justification: PipeJustification;
}

export interface PipeStepTransition {
  id: string;
  from_step_id: string;
  to_step_id: string;
  card_type: string;
}

export interface PipeDetail extends PipeSummary {
  steps: PipeStep[];
  transitions: PipeStepTransition[];
}

export interface CardPriority {
  id: string;
  key: string;
  name: string;
  level: number;
  is_default: boolean;
  active: boolean;
}

export interface PipeCardParty {
  id: string;
  label: string;
  document: string;
}

/** Dados da Cotacao do cartao (GetCardProposalMinimalInfo do OnPoint). */
export interface PipeCardOperation {
  id: string;
  number: number;
  proposal_number: string;
  proposal_status: string;
  stage: string;
  subject: string;
  policyholder: PipeCardParty;
  broker: PipeCardParty | null;
  insured: PipeCardParty | null;
  product: string;
  modality: string;
  modality_group: string;
  insured_amount: Money;
  premium: Money;
  applied_rate_bps: number;
  commission_bps: number;
  coverage_start: string | null;
  coverage_end: string | null;
  term_days: number | null;
  created_at: string;
}

export interface PipeCard {
  id: string;
  pipe: { id: string; key: string; name: string };
  step: { id: string; key: string; name: string; type: string };
  subject_type: string;
  subject_id: string;
  operation_id: string | null;
  card_type: string;
  title: string;
  owner_member_id: string | null;
  owner_label: string;
  priority: CardPriority | null;
  company_size: string;
  credit_class: string;
  entered_pipe_at: string;
  entered_step_at: string;
  due_at: string | null;
  closed_at: string | null;
  is_closed: boolean;
  is_overdue: boolean;
  operation: PipeCardOperation | null;
  /** O tomador do cartao que nao e de cotacao (P-EST-2). */
  policyholder_id?: string | null;
  /** O tomador e o pedido do cartao que nao e de cotacao (P-EST-2). */
  subject?: PipeCardSubject | null;
}

export interface PipeCardTarget {
  step: { id: string; key: string; name: string; type: string };
  allowed: boolean;
  blocked_reason: string;
  operation_stage: string;
  operation_label: string;
  analysis_status: string;
  requires_reason: boolean;
  justification: PipeJustification;
}

export interface PipeCardRule {
  id: string;
  rule_id: string;
  message: string;
  source: string;
  created_at: string;
  /** A execucao do motor que mandou a regra e a raia (P-EST-2). */
  engine_run_id?: string | null;
  lane?: string;
  outcome?: string;
}

export interface PipeCardDetail extends PipeCard {
  targets: PipeCardTarget[];
  rules: PipeCardRule[];
  justification_parameters: PipeJustification;
  /** Quadro Grupos Economicos (Cadastro e cartao de tomador, P-EST-2). */
  economic_groups?: PipeCardSubjectGroups | null;
  /** Socios, Administradores e Representantes (PEP, P-EST-2). */
  pep_people?: PipeCardSubjectPeople | null;
  /** Envios ao corretor do cartao (P-EST-2). */
  dispatches?: BrokerDispatch[];
}

export interface PipeCardEvent {
  id: string;
  kind:
    | 'entrada'
    | 'movimento'
    | 'responsavel'
    | 'prioridade'
    | 'saida'
    | 'regra'
    | 'envio_corretor';
  origin:
    'quadro' | 'operacao' | 'analise' | 'pendencia' | 'carga' | 'motor' | 'pedido';
  from_step: { id: string; name: string } | null;
  to_step: { id: string; name: string } | null;
  decline_reason_id: string | null;
  decline_reason_label: string;
  justification_text: string;
  actor_label: string;
  detail: Record<string, unknown>;
  created_at: string;
}

export interface PipeOwner {
  member_id: string;
  name: string;
  email: string;
}

export interface PipeOperationCards {
  current: PipeCard | null;
  cards: PipeCard[];
  indicators: {
    credit_card_open: boolean;
    underwriting_card_open: boolean;
    underwriting_card_id: string | null;
    has_open_card: boolean;
    economic_group_card_open: boolean;
  };
}

export interface PipeCardFilters extends ListParams {
  step_id?: string;
  priority_id?: string;
  owner_member_id?: string;
  company_size?: string;
  credit_class?: string;
  overdue?: boolean;
  include_closed?: string;
}

export interface PipeMoveInput {
  to_step_id: string;
  decline_reason_id?: string | null;
  justification_text?: string;
}

export interface CardPriorityInput {
  name?: string;
  level?: number;
  is_default?: boolean;
  active?: boolean;
}

export interface PipeEngineDomains {
  engine_sizes: string[];
  registry_sizes: string[];
  classes: string[];
  risk_classes: string[];
}

export const pipes = {
  list: (signal?: AbortSignal) => api.get<PipeSummary[]>('/api/v1/pipes', signal),
  get: (pipeId: string, signal?: AbortSignal) =>
    api.get<PipeDetail>(`/api/v1/pipes/${pipeId}`, signal),
  cards: (pipeId: string, filters: PipeCardFilters, signal?: AbortSignal) =>
    requestPage<PipeCard>(`/api/v1/pipes/${pipeId}/cards${query(filters)}`, signal),
  owners: (pipeId: string, signal?: AbortSignal) =>
    api.get<PipeOwner[]>(`/api/v1/pipes/${pipeId}/owners`, signal),
  card: (cardId: string, signal?: AbortSignal) =>
    api.get<PipeCardDetail>(`/api/v1/pipe-cards/${cardId}`, signal),
  updateCard: (
    cardId: string,
    input: { owner_member_id?: string | null; priority_id?: string },
  ) => api.patch<PipeCardDetail>(`/api/v1/pipe-cards/${cardId}`, input),
  move: (cardId: string, input: PipeMoveInput) =>
    api.post<PipeCardDetail>(`/api/v1/pipe-cards/${cardId}/moves`, input),
  events: (cardId: string, signal?: AbortSignal) =>
    api.get<PipeCardEvent[]>(`/api/v1/pipe-cards/${cardId}/events`, signal),
  operationCards: (operationId: string, signal?: AbortSignal) =>
    api.get<PipeOperationCards>(`/api/v1/operations/${operationId}/card`, signal),
  priorities: (includeInactive = false, signal?: AbortSignal) =>
    api.get<CardPriority[]>(
      `/api/v1/card-priorities${query({ include_inactive: includeInactive })}`,
      signal,
    ),
  createPriority: (input: CardPriorityInput) =>
    api.post<CardPriority>('/api/v1/settings/card-priorities', input),
  updatePriority: (priorityId: string, input: CardPriorityInput) =>
    api.patch<CardPriority>(`/api/v1/settings/card-priorities/${priorityId}`, input),
  updateStepSla: (
    stepId: string,
    input: { sla_days: number | null; sla_calendar: 'uteis' | 'corridos' },
  ) => api.patch<PipeStep>(`/api/v1/settings/pipe-steps/${stepId}`, input),
  engineDomains: (signal?: AbortSignal) =>
    api.get<PipeEngineDomains>('/api/v1/credit-engine/domains', signal),

  // --- P-EST-2: cartao manual, Follow up do cartao, envio ao corretor e raias -------
  openCard: (pipeId: string, input: PipeCardOpenInput) =>
    api.post<PipeCardDetail>(`/api/v1/pipes/${pipeId}/cards`, input),
  updateSettings: (pipeId: string, input: { engine_cards_enabled: boolean }) =>
    api.patch<PipeSummary>(`/api/v1/settings/pipes/${pipeId}`, input),
  cardComments: (cardId: string, signal?: AbortSignal) =>
    api.get<PipeCardComment[]>(`/api/v1/pipe-cards/${cardId}/comments`, signal),
  addCardComment: (cardId: string, input: { body: string; title?: string }) =>
    api.post<PipeCardComment>(`/api/v1/pipe-cards/${cardId}/comments`, input),
  sendToBroker: (cardId: string, input: BrokerDispatchInput) =>
    api.post<BrokerDispatch>(`/api/v1/pipe-cards/${cardId}/send-to-broker`, input),
  sendDocumentRequests: (
    cardId: string,
    input: { document_ids?: string[]; note?: string },
  ) =>
    api.post<BrokerDispatch>(
      `/api/v1/pipe-cards/${cardId}/document-requests/send`,
      input,
    ),
  sendPipeDocumentRequests: (
    pipeId: string,
    input: { card_ids?: string[]; note?: string },
  ) =>
    api.post<BrokerDispatchBatch>(
      `/api/v1/pipes/${pipeId}/document-requests/send`,
      input,
    ),
  brokerDispatches: (filters: BrokerDispatchFilters, signal?: AbortSignal) =>
    requestPage<BrokerDispatch>(`/api/v1/broker-dispatches${query(filters)}`, signal),
  engineRuns: (operationId: string, runKind: string, signal?: AbortSignal) =>
    api.get<PipeEngineRun[]>(
      `/api/v1/operations/${operationId}/credit-engine/runs${query({ run_kind: runKind })}`,
      signal,
    ),
};

// --- Esteiras P-EST-2: sujeitos novos, envio ao corretor e revisao de limite --------
//
// Ver docs/ADR/0056-esteiras-sujeitos-e-envio.md.

/** O tomador e o pedido do cartao (Dados da Solicitacao de Nomeacao e Revisao de Limite). */
export interface PipeCardSubject {
  policyholder: PipeCardParty;
  broker: PipeCardParty | null;
  status: string;
  kind: string;
  requested_at: string | null;
  requested_label: string;
  requested_by_broker: boolean;
  note: string;
  decision_note: string;
  decline_reason: string;
  decided_label: string;
  decided_at: string | null;
  valid_until: string | null;
  instrument: string;
  letter_document_id: string | null;
  target_nomination_id: string | null;
  modality_group: string;
  requested_amount: Money | null;
  current_limit: Money | null;
  reviewed_limit: Money | null;
  operation_id: string | null;
}

export interface PipeCardSubjectGroup {
  id: string;
  economic_group_id: string | null;
  name: string;
  document: string;
  participation_bps: number;
  group_limit: Money | null;
  created_at: string;
}

export interface PipeCardSubjectGroups {
  available: boolean;
  reason: string;
  items: PipeCardSubjectGroup[];
}

export interface PipeCardSubjectPerson {
  id: string;
  role: 'socio' | 'administrador' | 'representante';
  name: string;
  document: string;
  participation_bps: number | null;
  is_pep: boolean;
  is_majority: boolean;
}

export interface PipeCardSubjectPeople {
  available: boolean;
  reason: string;
  partners: PipeCardSubjectPerson[];
  administrators: PipeCardSubjectPerson[];
  representatives: PipeCardSubjectPerson[];
  pep_count: number;
}

export interface PipeCardOpenInput {
  subject_type: 'operacao' | 'tomador';
  subject_id: string;
  card_type?: string;
  title?: string;
}

export interface PipeCardComment {
  id: string;
  card_id: string;
  body: string;
  title: string;
  source: string;
  author_label: string;
  sent_to_broker_at: string | null;
  sent_to_broker_label: string;
  created_at: string;
}

export interface BrokerDispatch {
  id: string;
  card_id: string;
  pipe: { id: string; key: string; name: string };
  subject_type: string;
  subject_id: string;
  operation_id: string | null;
  policyholder_id: string | null;
  broker: { id: string; name: string };
  kind: 'follow_up' | 'documentos';
  title: string;
  message: string;
  comment_ids: string[];
  document_ids: string[];
  /** O aviso por e-mail e regra sem fonte (EMI-002): sempre pendente_de_regra. */
  email_notification: 'pendente_de_regra';
  sent_label: string;
  sent_at: string;
}

export interface BrokerDispatchInput {
  body?: string;
  title?: string;
  comment_ids?: string[];
}

export interface BrokerDispatchBatch {
  sent: BrokerDispatch[];
  skipped: Array<{ card_id: string; reason: string }>;
}

export interface BrokerDispatchFilters extends ListParams {
  card_id?: string;
  operation_id?: string;
  policyholder_id?: string;
}

/** Execucao do motor com o tipo (Historico do motor por tipo de execucao). */
export interface PipeEngineRun {
  id: string;
  run_kind: string;
  is_last: boolean;
  lanes: Array<{ lane: string; rules: string[] }>;
  manual_approval_allowed: boolean | null;
  force_documents: boolean | null;
  mode: string;
  final_result: string;
  calculated_result: string;
  pendency_count: number;
  simulated: boolean;
  requested_label: string;
  created_at: string;
}

/** Pedido de revisao de limite (cartao Revisao de Limite da esteira Credito). */
export interface LimitReviewRequest {
  id: string;
  policyholder: { id: string; label: string };
  policyholder_document: string;
  modality_group: string;
  requested_amount: Money;
  current_limit: Money | null;
  reviewed_limit: Money | null;
  reason: string;
  operation_id: string | null;
  broker: { id: string; label: string } | null;
  broker_document: string;
  status: 'pendente' | 'concluida' | 'recusada' | 'cancelada';
  requested_label: string;
  requested_by_broker: boolean;
  requested_at: string;
  decision_note: string;
  decline_reason_id: string | null;
  decline_reason: string;
  decided_label: string;
  decided_at: string | null;
  /** Sempre falso: o limite muda pela alcada do limite do tomador. */
  limit_changed: boolean;
}

export interface LimitReviewInput {
  policyholder_id: string;
  modality_group: string;
  requested_amount: Money;
  reason?: string;
  operation_id?: string | null;
}

export interface LimitReviewFilters extends ListParams {
  policyholder_id?: string;
  status?: string;
}

export const limitReviews = {
  list: (filters: LimitReviewFilters, signal?: AbortSignal) =>
    requestPage<LimitReviewRequest>(
      `/api/v1/credit-limit-review-requests${query(filters)}`,
      signal,
    ),
  get: (requestId: string, signal?: AbortSignal) =>
    api.get<LimitReviewRequest>(
      `/api/v1/credit-limit-review-requests/${requestId}`,
      signal,
    ),
  request: (input: LimitReviewInput) =>
    api.post<LimitReviewRequest>('/api/v1/credit-limit-review-requests', input),
  decide: (
    requestId: string,
    input: {
      decision: 'concluir' | 'recusar';
      note?: string;
      decline_reason_id?: string | null;
      /** Limite aprovado na conclusao. Ausente: o limite nao muda. */
      approved_limit?: Money;
    },
  ) =>
    api.post<LimitReviewRequest>(
      `/api/v1/credit-limit-review-requests/${requestId}/decision`,
      input,
    ),
  cancel: (requestId: string, note: string) =>
    api.post<LimitReviewRequest>(
      `/api/v1/credit-limit-review-requests/${requestId}/cancellation`,
      { note },
    ),
};
